import React, { useState, useEffect } from 'react';
import { categoryService } from '../services/categoryService';

function CategoryForm({ category, parentCategory, onSave, onCancel }) {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#ff6b35');
  const [icon, setIcon] = useState('💰');
  const [budgetLimit, setBudgetLimit] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [selectedParentId, setSelectedParentId] = useState('');
  const [availableParents, setAvailableParents] = useState([]);
  const [loading, setLoading] = useState(false);

  const predefinedColors = [
    // Vibrant and contrasting colors for better visibility
    '#FF6B35', // Orange (primary)
    '#E74C3C', // Red
    '#2ECC71', // Green
    '#3498DB', // Blue
    '#9B59B6', // Purple
    '#F39C12', // Yellow/Gold
    '#E91E63', // Pink
    '#00BCD4', // Cyan
    '#FF5722', // Deep Orange
    '#4CAF50', // Light Green
    '#2196F3', // Light Blue
    '#673AB7', // Deep Purple
    '#FF9800', // Amber
    '#795548', // Brown
    '#607D8B', // Blue Grey
    '#000000', // Black
    '#FFFFFF', // White
    '#FF1744', // Bright Red
    '#00E676', // Bright Green
    '#1DE9B6', // Teal
    '#FF6D00', // Bright Orange
    '#651FFF', // Deep Purple
    '#FFEA00', // Bright Yellow
    '#C51162'  // Dark Pink
  ];

  const predefinedIcons = [
    // Money & Finance
    '💰', '💳', '💵', '🏦', '📊', '💎', '🪙', '💸',
    
    // Food & Dining
    '🍽️', '🍕', '🍔', '🍜', '🥗', '🍰', '☕', '🍺', '🥤', '🍎',
    
    // Transportation
    '🚗', '🚌', '✈️', '🚲', '🛵', '🚕', '⛽', '🚇', '🛻', '🏍️',
    
    // Shopping & Retail
    '🛍️', '👕', '👟', '💄', '🎁', '🛒', '🏪', '🛵', '📱', '💻',
    
    // Entertainment & Leisure
    '🎬', '🎮', '🎵', '🎯', '🎪', '🎨', '🎭', '🎸', '🎲', '🎳',
    
    // Health & Fitness
    '🏥', '💊', '🏋️', '🧘', '🏃', '⚽', '🏀', '🎾', '🏊', '🚴',
    
    // Home & Utilities
    '🏠', '💡', '🔧', '🧹', '🛏️', '🚿', '🔥', '❄️', '📺', '🪑',
    
    // Education & Work
    '📚', '✏️', '💼', '🖥️', '📝', '🎓', '📖', '🖊️', '📋', '📊',
    
    // Travel & Vacation
    '🧳', '🏖️', '🗺️', '🏨', '🎒', '📷', '🏔️', '🌴', '🛫', '🚢',
    
    // Miscellaneous & Fun
    '📦', '🎈', '🌟', '⭐', '🔮', '🎪', '🎊', '🎉', '🌈', '🦄',
    '🐶', '🐱', '🌸', '🌺', '🍀', '🌙', '☀️', '⚡', '🔥', '💫'
  ];

  useEffect(() => {
    const loadParents = async () => {
      try {
        // Get categories using the regular getCategories method
        const data = await categoryService.getCategories();
        
        if (!data || !data.categories) {
          setAvailableParents([]);
          return;
        }
        
        // Extract all categories (flatten if hierarchical)
        const allCategories = [];
        data.categories.forEach(cat => {
          allCategories.push(cat);
          if (cat.subcategories) {
            cat.subcategories.forEach(sub => allCategories.push(sub));
          }
        });
        
        // Filter to get only potential parents (top-level categories)
        const potentialParents = allCategories.filter(cat => {
          // Don't allow category to be its own parent
          if (category && cat.id === category.id) return false;
          // Only allow top-level categories as parents (no parentId)
          if (cat.parentId) return false;
          return true;
        });
        
        // Sort potential parents alphabetically by name
        potentialParents.sort((a, b) => a.name.localeCompare(b.name));
        
        setAvailableParents(potentialParents);
        
      } catch (error) {
        console.error('Failed to load available parents:', error);
        setAvailableParents([]);
      }
    };

    loadParents();
    
    if (category) {
      setName(category.name);
      setColor(category.color);
      setIcon(category.icon);
      setBudgetLimit(category.budgetLimit ? category.budgetLimit.toString() : '');
      setKeywords(category.keywords || []);
      setSelectedParentId(category.parentId || '');
    } else if (parentCategory) {
      // If creating a subcategory, set the parent
      setSelectedParentId(parentCategory.id);
    }
  }, [category, parentCategory]);

  const addKeyword = () => {
    const keyword = newKeyword.trim().toLowerCase();
    if (keyword && !keywords.includes(keyword)) {
      setKeywords([...keywords, keyword]);
      setNewKeyword('');
    }
  };

  const removeKeyword = (keywordToRemove) => {
    setKeywords(keywords.filter(keyword => keyword !== keywordToRemove));
  };

  const handleKeywordKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addKeyword();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      return;
    }

    setLoading(true);
    
    try {
      await onSave({
        name: name.trim(),
        color,
        icon,
        budgetLimit: budgetLimit ? parseFloat(budgetLimit) : null,
        keywords: keywords,
        parentId: selectedParentId || null
      });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)'
    }}>
      <div className="card" style={{ 
        width: '500px', 
        margin: 0,
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h3 style={{ 
          color: '#ff6b35', 
          marginBottom: '24px',
          fontSize: '1.5rem',
          fontWeight: '600',
          textAlign: 'center'
        }}>
          {category ? 'Edit Category' : parentCategory ? `Add Subcategory to ${parentCategory.name}` : 'Add New Category'}
        </h3>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Category Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter category name"
              required
              disabled={category?.isDefault}
            />
            {category?.isDefault && (
              <small style={{ color: '#888', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Default category names cannot be changed
              </small>
            )}
          </div>

          <div className="form-group">
            <label>Parent Category (Optional)</label>
            <select
              value={selectedParentId}
              onChange={(e) => setSelectedParentId(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                background: '#2a2a2a',
                border: '1px solid #404040',
                borderRadius: '8px',
                color: '#f0f0f0',
                fontSize: '14px'
              }}
              disabled={parentCategory} // Disable if creating subcategory
            >
              <option value="">No parent (top-level category)</option>
              {availableParents && availableParents.length > 0 ? (
                availableParents.map(parent => (
                  <option key={parent.id} value={parent.id}>
                    {parent.icon} {parent.name}
                  </option>
                ))
              ) : (
                <option disabled>No categories available as parents</option>
              )}
            </select>
            {parentCategory && (
              <small style={{ color: '#2ecc71', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Creating subcategory under: {parentCategory.icon} {parentCategory.name}
              </small>
            )}
            <small style={{ color: '#888', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              Select a parent category to create a subcategory. Only top-level categories can be parents.
            </small>
          </div>
          
          <div className="form-group">
            <label>Icon</label>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(10, 1fr)', 
              gap: '6px',
              marginBottom: '12px',
              maxHeight: '200px',
              overflowY: 'auto',
              padding: '8px',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '8px'
            }}>
              {predefinedIcons.map(iconOption => (
                <button
                  key={iconOption}
                  type="button"
                  onClick={() => setIcon(iconOption)}
                  style={{
                    background: icon === iconOption ? 'rgba(255, 107, 53, 0.4)' : 'rgba(255, 107, 53, 0.1)',
                    border: icon === iconOption ? '2px solid #ff6b35' : '1px solid #404040',
                    borderRadius: '6px',
                    padding: '6px',
                    fontSize: '18px',
                    cursor: 'pointer',
                    aspectRatio: '1',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    if (icon !== iconOption) {
                      e.target.style.background = 'rgba(255, 107, 53, 0.2)';
                      e.target.style.transform = 'scale(1.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (icon !== iconOption) {
                      e.target.style.background = 'rgba(255, 107, 53, 0.1)';
                      e.target.style.transform = 'scale(1)';
                    }
                  }}
                >
                  {iconOption}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="Or enter custom emoji (🎯, 🌟, etc.)"
              style={{ marginTop: '8px' }}
            />
          </div>
          
          <div className="form-group">
            <label>Budget Limit (Optional)</label>
            <input
              type="number"
              value={budgetLimit}
              onChange={(e) => setBudgetLimit(e.target.value)}
              placeholder="Enter monthly budget limit (e.g. 500)"
              min="0"
              step="0.01"
            />
            <small style={{ color: '#888', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              Set a monthly spending limit for this category. Leave empty for no limit.
            </small>
          </div>

          <div className="form-group">
            <label>Keywords for Auto-Categorization</label>
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              marginBottom: '12px',
              alignItems: 'center'
            }}>
              <input
                type="text"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyPress={handleKeywordKeyPress}
                placeholder="Add keyword (e.g. 'restaurant', 'gas', 'grocery')"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={addKeyword}
                disabled={!newKeyword.trim() || keywords.includes(newKeyword.trim().toLowerCase())}
                style={{
                  background: 'rgba(255, 107, 53, 0.2)',
                  border: '1px solid #ff6b35',
                  color: '#ff6b35',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: !newKeyword.trim() || keywords.includes(newKeyword.trim().toLowerCase()) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: !newKeyword.trim() || keywords.includes(newKeyword.trim().toLowerCase()) ? 0.5 : 1,
                  transition: 'all 0.2s ease'
                }}
              >
                Add
              </button>
            </div>
            
            {keywords.length > 0 && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
                padding: '12px',
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '8px',
                marginBottom: '8px'
              }}>
                {keywords.map((keyword, index) => (
                  <span
                    key={index}
                    style={{
                      background: 'rgba(255, 107, 53, 0.2)',
                      border: '1px solid rgba(255, 107, 53, 0.4)',
                      color: '#ff6b35',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    {keyword}
                    <button
                      type="button"
                      onClick={() => removeKeyword(keyword)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ff6b35',
                        cursor: 'pointer',
                        fontSize: '14px',
                        padding: '0',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 71, 87, 0.3)';
                        e.target.style.color = '#ff4757';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'none';
                        e.target.style.color = '#ff6b35';
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            
            <small style={{ color: '#888', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              Keywords help automatically categorize imported expenses. When importing CSV files, expenses containing these keywords will be assigned to this category.
            </small>
          </div>

          <div className="form-group">
            <label>Color</label>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(8, 1fr)', 
              gap: '8px',
              marginBottom: '12px',
              padding: '8px',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '8px'
            }}>
              {predefinedColors.map(colorOption => (
                <button
                  key={colorOption}
                  type="button"
                  onClick={() => setColor(colorOption)}
                  style={{
                    background: colorOption,
                    border: color === colorOption ? '3px solid #fff' : colorOption === '#FFFFFF' ? '2px solid #404040' : '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    width: '40px',
                    height: '40px',
                    cursor: 'pointer',
                    boxShadow: color === colorOption ? '0 0 0 2px #ff6b35, 0 2px 8px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.2)',
                    transition: 'all 0.2s ease',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    if (color !== colorOption) {
                      e.target.style.transform = 'scale(1.1)';
                      e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (color !== colorOption) {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
                    }
                  }}
                >
                  {color === colorOption && (
                    <span style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: colorOption === '#FFFFFF' || colorOption === '#FFEA00' ? '#000' : '#fff',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}>
                      ✓
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                style={{
                  width: '60px',
                  height: '40px',
                  border: '2px solid #404040',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  cursor: 'pointer'
                }}
              />
              <span style={{ color: '#b0b0b0', fontSize: '14px' }}>
                Or pick a custom color
              </span>
            </div>
          </div>

          <div style={{ 
            background: `linear-gradient(135deg, ${color}20, ${color}10)`,
            border: `2px solid ${color}40`,
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: '-10px',
              right: '-10px',
              width: '40px',
              height: '40px',
              backgroundColor: color,
              borderRadius: '50%',
              opacity: 0.2
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-15px',
              left: '-15px',
              width: '30px',
              height: '30px',
              backgroundColor: color,
              borderRadius: '50%',
              opacity: 0.1
            }}></div>
            
            <div style={{ 
              fontSize: '48px', 
              marginBottom: '12px',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              position: 'relative',
              zIndex: 1
            }}>
              {icon}
            </div>
            <div style={{ 
              color: '#f0f0f0', 
              fontWeight: '700',
              fontSize: '18px',
              marginBottom: '8px',
              position: 'relative',
              zIndex: 1
            }}>
              {name || 'Category Name'}
            </div>
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              position: 'relative',
              zIndex: 1
            }}>
              <div style={{ 
                width: '24px', 
                height: '24px', 
                backgroundColor: color,
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                border: '2px solid rgba(255,255,255,0.2)'
              }}></div>
              <span style={{ 
                color: '#b0b0b0', 
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {color.toUpperCase()}
              </span>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
            <button 
              type="submit" 
              className="btn" 
              style={{ flex: 1 }}
              disabled={loading || !name.trim()}
            >
              {loading ? 'Saving...' : (category ? 'Update Category' : 'Add Category')}
            </button>
            <button 
              type="button" 
              onClick={onCancel}
              className="btn" 
              style={{ 
                flex: 1, 
                background: 'linear-gradient(135deg, #666, #555)',
                boxShadow: '0 2px 8px rgba(102, 102, 102, 0.3)'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CategoryForm;